import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  VStack,
  keyframes,
} from "@chakra-ui/react";
import { useReducedMotion } from "framer-motion";
import type { ReactElement } from "react";

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-15px); }
`;

interface StepCardDarkProps {
  icon: ReactElement;
  titlePreface: string;
  title: string;
  paragraphs: string[];
}

export default function StepCardDark({
  icon,
  titlePreface,
  title,
  paragraphs,
}: StepCardDarkProps) {
  const shouldReduceMotion = useReducedMotion();
  const floatAnimation = shouldReduceMotion
    ? undefined
    : `${float} 6s ease-in-out infinite`;

  return (
    <Box w="full" position="relative" overflow="hidden" bg="#0a0f1a">
      {/* Gradient accents */}
      <Box
        position="absolute"
        top="20%"
        right="-5%"
        w="300px"
        h="300px"
        borderRadius="full"
        bg="linear-gradient(135deg, rgba(255, 79, 0, 0.15), rgba(255, 120, 50, 0.05))"
        filter="blur(80px)"
        animation={floatAnimation}
      />
      <Box
        position="absolute"
        bottom="10%"
        left="-5%"
        w="200px"
        h="200px"
        borderRadius="full"
        bg="linear-gradient(135deg, rgba(0, 100, 255, 0.1), rgba(100, 150, 255, 0.05))"
        filter="blur(60px)"
      />

      {/* Grid pattern overlay */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        opacity={0.02}
        backgroundImage="linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)"
        backgroundSize="50px 50px"
      />

      <Container maxW="6xl" position="relative" zIndex={1}>
        <Flex
          direction="column"
          align="center"
          gap={8}
          py={{ base: 20, md: 28 }}
          textAlign="center"
        >
          {/* Step number and icon */}
          <Box
            w={24}
            h={24}
            borderRadius="2xl"
            bg="rgba(255, 255, 255, 0.05)"
            border="1px solid rgba(255, 255, 255, 0.1)"
            display="flex"
            alignItems="center"
            justifyContent="center"
            position="relative"
            backdropFilter="blur(10px)"
          >
            <Box color="brand.500">{icon}</Box>
          </Box>

          {/* Content */}
          <VStack spacing={4} maxW="3xl">
            <Text
              color="brand.500"
              fontSize="sm"
              fontWeight={700}
              letterSpacing="0.15em"
              textTransform="uppercase"
            >
              {titlePreface}
            </Text>

            <Heading
              color="white"
              fontWeight={800}
              fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
              lineHeight="1.2"
              letterSpacing="-0.02em"
            >
              {title}
            </Heading>

            <VStack spacing={4} pt={2}>
              {paragraphs.map((paragraph) => (
                <Text
                  key={paragraph.substring(0, 50)}
                  color="text.on-black-secondary"
                  fontSize={{ base: "md", md: "lg" }}
                  lineHeight="1.8"
                >
                  {paragraph}
                </Text>
              ))}
            </VStack>
          </VStack>
        </Flex>
      </Container>
    </Box>
  );
}
